import { OverlayRef } from "@angular/cdk/overlay";
import { Observable, Subject } from "rxjs";

import { VdsAlertContainerComponent } from "./alert-container";

/**
 * Event that is emitted when the alert is dismissed by a user action
 */
export interface AlertDismissed {
    dismissedByAction: boolean;
}

const MAX_TIMEOUT = Math.pow(2, 31) - 1;

export class VdsAlertRef<T> {
    /** The instance of the component making up the contents of the alert */
    instance!: T;
    containerInstance: VdsAlertContainerComponent;

    /** Subject for notifying the user that the alert has been dismissed */
    private readonly _afterDismissed = new Subject<AlertDismissed>();

    /** Subject for notifying the user that the alert has been opened and appeared */
    private readonly _afterOpened = new Subject<void>();

    /** Subject for notifying the user that the alert action was called */
    private readonly _onAction = new Subject<void>();

    /**
     * Timeout ID for the duration setTimeout call. Used to clear the timeout, if the
     * alert is dismissed manually
     */
    private _durationTimeoutId: ReturnType<typeof setTimeout> | undefined;

    /** Whether the alert was dismissed using the action button */
    private _dismissedByAction = false;

    constructor(
        containerInstance: VdsAlertContainerComponent,
        private _overlayRef: OverlayRef
    ) {
        this.containerInstance = containerInstance;
        containerInstance._onExit.subscribe(() => this._finishDismiss());
    }

    dismiss(): void {
        if (!this._afterDismissed.closed) {
            this.containerInstance.exit();
        }
        clearTimeout(this._durationTimeoutId);
    }

    /** Marks the alert action clicked */
    dismissWithAction(): void {
        if (!this._onAction.closed) {
            this._dismissedByAction = true;
            this._onAction.next();
            this._onAction.complete();
            this.dismiss();
        }
    }

    /** Dismisses the alert after some duration */
    _dismissAfter(duration: number): void {
        this._durationTimeoutId = setTimeout(() => this.dismiss(), Math.min(duration, MAX_TIMEOUT)) as any as number;
    }

    /** Marks the alert as opened */
    _open(): void {
        if (!this._afterOpened.closed) {
            this._afterOpened.next();
            this._afterOpened.complete();
        }
    }

    /** Cleans up the DOM after closing. */
    private _finishDismiss(): void {
        this._overlayRef.dispose();

        if (!this._onAction.closed) {
            this._onAction.complete();
        }

        this._afterDismissed.next({ dismissedByAction: this._dismissedByAction });
        this._afterDismissed.complete();
        this._dismissedByAction = false;
    }

    /** Gets an observable that is notified when the snack bar is finished closing. */
    afterDismissed(): Observable<AlertDismissed> {
        return this._afterDismissed;
    }

    /** Gets an observable that is notified when the snack bar has opened and appeared. */
    afterOpened(): Observable<void> {
        return this.containerInstance._onEnter;
    }

    /** Gets an observable that is notified when the snack bar action is called. */
    onAction(): Observable<void> {
        return this._onAction;
    }
}
